/* GASSISA
=========================================================================================
* Descripción: Mapa de ayudas por módulo del sistema
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 02/06/2026 | Lic. Jose Luis Aragon Cervantes  | Normalización de ayudas por ruta activa
 *      2       | 03/06/2026 | Lic. Jose Luis Aragon Cervantes  | Ayuda de laboratorio Ticket ventas master
 *      3       | 30/08/2026 | Lic. Jose Luis Aragon Cervantes  | Atajos: eliminar todo Alt+F12, reimpresion Alt+P
 * ---------------------------------------------------------------------------------------
 */

const regresoDashboard = { key: "F4", action: "Regresar al dashboard" };

const step = (key, title, content) => ({ key: String(key), title, content });
const shortcut = (key, action) => ({ key, action });
const button = (name, description, action) => ({ name, description, action });
const field = (name, description) => ({ name, description });

const makeHelp = ({
    title,
    description,
    howToStart,
    howToWork,
    howToEnd,
    howToPrint = "Este módulo no genera impresión directa.",
    steps = [],
    tips = [],
    shortcutKeys = [],
    menuButtons = [],
    fields = [],
}) => ({
    title,
    description,
    howToStart,
    howToWork,
    howToEnd,
    howToPrint,
    steps,
    tips,
    shortcutKeys: [...shortcutKeys, regresoDashboard],
    menuButtons,
    fields,
});

export const HelpConfigMap = {
    "/ventas": makeHelp({
        title: "Módulo de Venta Super",
        description: "Punto de venta principal para registrar artículos, aplicar descuentos, jalar preventas o cotizaciones, cobrar e imprimir tickets.",
        howToStart: "1. Verifique que la caja esté abierta.\n2. Mantenga el foco en el lector de códigos.\n3. Escanee artículos o use el buscador con Alt+F1.\n4. Asigne cliente o tarjeta si se requiere descuento.",
        howToWork: "Escanee artículos para agregarlos a la venta. Use Alt+F1 para buscar artículos, Alt+F3 para descuentos, Alt+J para preventas firmadas y Alt+O para cotizaciones. Las preventas sin firma no deben aparecer para venta.",
        howToEnd: "1. Presione Alt+F10 para cobrar.\n2. Seleccione método de pago.\n3. Confirme el cobro.\n4. El ticket se imprime al finalizar.",
        howToPrint: "El ticket se imprime automáticamente al cobrar. Use Reimprimir para emitir copias de ventas anteriores.",
        steps: [
            step(1, "Escanear artículos", "Use el lector o el buscador manual para cargar partidas."),
            step(2, "Aplicar descuentos", "Use la herramienta de descuento y valide tarjeta o permiso si aplica."),
            step(3, "Jalar preventas", "Use Alt+J para cargar preventas firmadas por vendedor."),
            step(4, "Cobrar", "Use Alt+F10 para abrir cobro general y terminar la venta."),
            step(5, "Reimprimir", "Use el botón de reimpresión para tickets ya emitidos."),
        ],
        tips: [
            "El lector debe conservar el foco después de registrar artículos.",
            "Las tarjetas de cliente se detectan para aplicar descuentos autorizados.",
            "El total en letra y totales se ajustan al panel lateral.",
        ],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículos"),
            shortcut("Alt+F3", "Aplicar descuentos"),
            shortcut("Alt+F10", "Cobrar venta"),
            shortcut("Alt+J", "Jalar preventas"),
            shortcut("Alt+O", "Jalar cotizaciones"),
            shortcut("Alt+F7", "Eliminar partida"),
            shortcut("Alt+F12", "Eliminar venta"),
            shortcut("Alt+P", "Reimprimir remisiones"),
            shortcut("Alt+V", "Ir a devoluciones"),
        ],
        menuButtons: [
            button("Artículos", "Abre buscador de artículos", "buscar"),
            button("Descuento", "Aplica descuentos autorizados", "descuento"),
            button("Reimprimir", "Reimprime tickets", "reimprimir"),
            button("Menu", "Abre acciones adicionales", "menu"),
        ],
        fields: [
            field("Lector códigos", "Campo principal para escanear artículos."),
            field("Totales", "Muestra descuento, subtotal, IVA, total y total en letra."),
            field("Tabla de partidas", "Lista de artículos capturados en la venta."),
        ],
    }),

    "/preventas": makeHelp({
        title: "Módulo de Preventas",
        description: "Captura pedidos pendientes de cobro. Las preventas deben firmarse para poder recuperarse posteriormente en Venta Super.",
        howToStart: "1. Verifique caja y lector.\n2. Escanee o busque artículos.\n3. Revise cantidades y totales.\n4. Firme la preventa al finalizar.",
        howToWork: "Capture artículos como en venta. Use Alt+F1 para buscar. La firma de vendedor/cliente formaliza la preventa y habilita su recuperación desde venta.",
        howToEnd: "1. Presione Alt+F10 para firmar.\n2. Seleccione o capture vendedor/cliente.\n3. Confirme firma.\n4. La preventa queda lista para recuperación.",
        howToPrint: "La reimpresión de preventas firmadas se realiza desde reimpresión de caja.",
        steps: [
            step(1, "Registrar artículos", "Escanee o busque artículos para integrar la preventa."),
            step(2, "Editar partidas", "Seleccione una partida para ajustar cantidades si es necesario."),
            step(3, "Firmar", "Use Alt+F10 para firmar la preventa."),
            step(4, "Recuperar", "Use acciones de recuperación para retomar preventas firmadas."),
        ],
        tips: [
            "Una preventa sin firma no debe estar disponible para Venta Super.",
            "El panel lateral conserva el lector y totales visibles.",
        ],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículos"),
            shortcut("Alt+F10", "Firmar preventa"),
            shortcut("Alt+F7", "Eliminar partida"),
            shortcut("Alt+F12", "Eliminar preventa"),
        ],
        menuButtons: [
            button("Artículos", "Busca artículos", "buscar"),
            button("Firmar", "Firma la preventa", "firmar"),
            button("Menu", "Acciones adicionales", "menu"),
        ],
    }),

    "/apartados": makeHelp({
        title: "Módulo de Apartados",
        description: "Consulta apartados, registra abonos, emite tickets de abono y genera entrega definitiva con acuse de recibo.",
        howToStart: "1. Busque cliente o apartado.\n2. Seleccione una partida.\n3. Revise estado de cuenta y abonos.\n4. Use Abono o Entrega Definitiva según corresponda.",
        howToWork: "Abono abre Cobros Generales para capturar monto. Entrega Definitiva liquida o valida saldo, imprime ticket de abono si aplica, genera acuse y cambia el estado a ENTREGADO.",
        howToEnd: "1. Para abono normal, cobre desde Cobros Generales.\n2. Para entrega definitiva, use el botón correspondiente.\n3. La impresión no debe detener el registro; siempre se puede reimprimir.",
        howToPrint: "Abono imprime ticket de abono. Entrega definitiva imprime ticket de abono y acuse para firma. Reimpresiones están en acciones adicionales.",
        steps: [
            step(1, "Buscar apartado", "Filtre por cliente, concepto, grupo, artículo o medida."),
            step(2, "Estado de cuenta", "Cargue el apartado para ver abonos y saldo."),
            step(3, "Abonar", "Abra Cobros Generales y capture el monto del abono."),
            step(4, "Entrega definitiva", "Use el botón de entrega para finalizar y generar acuse."),
            step(5, "Reimpresiones", "Use acciones adicionales para reimprimir abonos o acuses."),
        ],
        tips: [
            "Si el monto cubre el 100%, use Entrega Definitiva.",
            "La impresión no cancela el proceso; use reimpresión si falla la impresora.",
            "El estado final del apartado es ENTREGADO.",
        ],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar cliente o apartado"),
        ],
        menuButtons: [
            button("Estado cuenta", "Carga detalle del apartado", "estadoCuenta"),
            button("Abono", "Abre Cobros Generales", "abono"),
            button("Entrega definitiva", "Finaliza apartado y genera acuse", "entrega"),
            button("Menu", "Reimpresiones y mantenimiento", "menu"),
        ],
    }),

    "/devoluciones": makeHelp({
        title: "Módulo de Devoluciones",
        description: "Procesa devoluciones vinculadas a ventas originales y genera comprobante.",
        howToStart: "1. Presione Nuevo.\n2. Busque venta original.\n3. Seleccione artículos y cantidades.\n4. Capture concepto de devolución.",
        howToWork: "Agregue las partidas a devolver, valide cantidades y conceptos, y aplique la devolución.",
        howToEnd: "1. Revise partidas.\n2. Presione Aplicar.\n3. Se genera ticket de devolución.",
        howToPrint: "El comprobante se imprime al aplicar la devolución.",
        steps: [
            step(1, "Nueva devolución", "Habilita búsqueda de venta original."),
            step(2, "Seleccionar partidas", "Capture cantidades a devolver."),
            step(3, "Aplicar", "Registra la devolución y genera ticket."),
        ],
        tips: ["Debe existir una venta original válida.", "El concepto ayuda a auditar la devolución."],
        shortcutKeys: [
            shortcut("Alt+F3", "Nueva devolución"),
            shortcut("Alt+A", "Aplicar devolución"),
            shortcut("Alt+F7", "Eliminar partida"),
            shortcut("Alt+F11", "Cancelar devolución"),
        ],
        menuButtons: [
            button("Nueva", "Inicia devolución", "nuevo"),
            button("Aplicar", "Aplica devolución", "aplicar"),
            button("Menu", "Acciones de eliminación", "menu"),
        ],
    }),

    "/articulos": makeHelp({
        title: "Catálogo de Articulos",
        description: "Alta, edición, búsqueda y mantenimiento de artículos, imagen principal, medidas, precios e IA de aprendizaje.",
        howToStart: "1. Presione Nuevo o Buscar.\n2. Capture datos generales.\n3. Configure familias, materiales, fabricantes y precios.\n4. Guarde los cambios.",
        howToWork: "Use las secciones del catálogo para completar datos del artículo. La foto principal se almacena en la tabla Artículos y puede usarse por existencias e IA.",
        howToEnd: "1. Revise campos requeridos.\n2. Guarde.\n3. Si requiere IA, use entrenamiento o sincronización desde el menu IA.",
        howToPrint: "Para etiquetas, use el módulo de Etiquetado.",
        steps: [
            step(1, "Buscar artículo", "Use Alt+F1 para localizar artículos."),
            step(2, "Nuevo artículo", "Use Alt+F3 para alta."),
            step(3, "Editar", "Use Alt+E para modificar datos."),
            step(4, "Guardar", "Use Alt+G para guardar."),
            step(5, "IA", "Use el menu IA si necesita entrenar o sincronizar aprendizaje."),
        ],
        tips: [
            "La clave del artículo debe ser única.",
            "La imagen principal se comparte con buscadores y existencias.",
            "Las medidas y precios son base para venta, verificador e inventarios.",
        ],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
            shortcut("Alt+F3", "Nuevo artículo"),
            shortcut("Alt+E", "Editar artículo"),
            shortcut("Alt+G", "Guardar artículo"),
            shortcut("Alt+F11", "Eliminar artículo"),
        ],
        menuButtons: [
            button("Buscar", "Busca artículos", "buscar"),
            button("Nuevo", "Crea artículo", "nuevo"),
            button("Editar", "Edita artículo", "editar"),
            button("Guardar", "Guarda artículo", "guardar"),
            button("IA", "Herramientas de aprendizaje", "ia"),
        ],
    }),

    "/clientes": makeHelp({
        title: "Catálogo de Clientes",
        description: "Mantenimiento de clientes, contactos, datos fiscales y descuentos.",
        howToStart: "1. Busque o cree cliente.\n2. Capture datos fiscales y contactos.\n3. Configure descuento si aplica.\n4. Guarde.",
        howToWork: "Los clientes pueden asignarse a ventas y sus tarjetas pueden usarse para descuentos autorizados.",
        howToEnd: "Guarde el cliente y verifique que quede activo para ventas.",
        steps: [
            step(1, "Buscar", "Localice clientes existentes."),
            step(2, "Nuevo", "Capture datos fiscales y contacto."),
            step(3, "Guardar", "Guarde cambios."),
        ],
        tips: ["Mantenga teléfono y contactos actualizados.", "El descuento de cliente se usa en Venta Super."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar cliente"),
            shortcut("Alt+F3", "Nuevo cliente"),
            shortcut("Alt+G", "Guardar cliente"),
        ],
        menuButtons: [
            button("Buscar", "Busca clientes", "buscar"),
            button("Nuevo", "Nuevo cliente", "nuevo"),
            button("Guardar", "Guarda cliente", "guardar"),
        ],
    }),

    "/proveedores": makeHelp({
        title: "Catálogo de Proveedores",
        description: "Registro y mantenimiento de proveedores usados por compras y cuentas por pagar.",
        howToStart: "1. Busque proveedor.\n2. Capture razón social, RFC, contactos y condiciones.\n3. Guarde.",
        howToWork: "Los proveedores se seleccionan en Compras. Mantenga clave, razón social y condiciones de crédito actualizadas.",
        howToEnd: "Guarde los cambios antes de registrar compras del proveedor.",
        steps: [
            step(1, "Buscar proveedor", "Localice proveedor existente."),
            step(2, "Nuevo proveedor", "Capture datos generales y fiscales."),
            step(3, "Guardar", "Guarde para usarlo en compras."),
        ],
        tips: ["Las compras a crédito dependen de proveedor y días de crédito.", "Revise RFC y razón social antes de guardar."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar proveedor"),
            shortcut("Alt+F3", "Nuevo proveedor"),
            shortcut("Alt+G", "Guardar proveedor"),
        ],
        menuButtons: [
            button("Buscar", "Busca proveedores", "buscar"),
            button("Nuevo", "Nuevo proveedor", "nuevo"),
            button("Guardar", "Guarda proveedor", "guardar"),
        ],
    }),

    "/almacenesexhibidores": makeHelp({
        title: "Catálogo de Almacenes y Exhibidores",
        description: "Mantenimiento de destinos y origenes de inventario.",
        howToStart: "1. Busque o cree almacen/exhibidor.\n2. Capture nombre.\n3. Defina estatus.\n4. Guarde.",
        howToWork: "Los almacenes se usan en entradas, salidas, traslados, compras, etiquetado y existencias.",
        howToEnd: "Guarde y verifique que el almacen quede activo para los módulos operativos.",
        steps: [
            step(1, "Nuevo almacen", "Capture nombre del almacen o exhibidor."),
            step(2, "Editar", "Modifique descripción o estatus."),
            step(3, "Guardar", "Actualiza catálogo operativo."),
        ],
        tips: ["Evite duplicados con espacios o minusculas.", "El nombre se normaliza en operaciones de inventario."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar"),
            shortcut("Alt+F3", "Nuevo"),
            shortcut("Alt+G", "Guardar"),
        ],
        menuButtons: [
            button("Buscar", "Busca almacenes", "buscar"),
            button("Nuevo", "Nuevo almacen", "nuevo"),
            button("Guardar", "Guarda almacen", "guardar"),
        ],
    }),

    "/corridas": makeHelp({
        title: "Catálogo de Corridas",
        description: "Define rangos de medidas/tallas para artículos.",
        howToStart: "1. Cree o busque corrida.\n2. Defina medida inicial, final e intervalo.\n3. Guarde.",
        howToWork: "Las corridas determinan que tallas se generan para artículos y buscadores.",
        howToEnd: "Guarde la corrida antes de asignarla a artículos.",
        steps: [
            step(1, "Nueva corrida", "Capture descripción y rango."),
            step(2, "Validar rango", "Revise medidas inicial/final e intervalos."),
            step(3, "Guardar", "Registra corrida."),
        ],
        tips: ["Evite rangos traslapados que generen llaves repetidas.", "Use intervalos consistentes por familia de producto."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar"),
            shortcut("Alt+F3", "Nuevo"),
            shortcut("Alt+G", "Guardar"),
        ],
        menuButtons: [
            button("Buscar", "Busca corridas", "buscar"),
            button("Nuevo", "Nueva corrida", "nuevo"),
            button("Guardar", "Guarda corrida", "guardar"),
        ],
    }),

    "/accesos": makeHelp({
        title: "Catálogo de Accesos",
        description: "Administra usuarios, permisos, menus asignados y alcances.",
        howToStart: "1. Busque usuario.\n2. Cree o edite datos de acceso.\n3. Configure menus, permisos y alcances.\n4. Guarde.",
        howToWork: "Los accesos controlan visibilidad de menus y permisos. Eliminar usuario limpia accesos relacionados.",
        howToEnd: "Guarde o elimine sólo después de revisar permisos y alcances.",
        steps: [
            step(1, "Buscar usuario", "Localice usuarios existentes."),
            step(2, "Configurar permisos", "Asigne menus, permisos y alcances."),
            step(3, "Guardar", "Aplica cambios de acceso."),
            step(4, "Eliminar", "Elimina acceso y datos relacionados."),
        ],
        tips: ["Revise alcances antes de guardar.", "La eliminación impacta menus asignados y permisos."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar"),
            shortcut("Alt+F3", "Nuevo"),
            shortcut("Alt+G", "Guardar"),
        ],
        menuButtons: [
            button("Buscar", "Busca usuarios", "buscar"),
            button("Nuevo", "Nuevo acceso", "nuevo"),
            button("Guardar", "Guarda acceso", "guardar"),
            button("Eliminar", "Elimina acceso", "eliminar"),
        ],
    }),

    "/existencias": makeHelp({
        title: "Existencias Almacenes/Exhibidores",
        description: "Consulta existencias por artículo, almacen y talla. Permite sincronizar kardex y revisar movimientos.",
        howToStart: "1. Seleccione almacen.\n2. Capture filtro.\n3. Presione Enter o busque.\n4. Revise existencias por talla.",
        howToWork: "La tabla muestra artículos y medidas. Las marcas visuales distinguen medida sin existencia o medida no manejada.",
        howToEnd: "Es un módulo de consulta; use Sincronizar si requiere recalcular existencias desde kardex.",
        howToPrint: "No imprime directamente; use reportes si necesita salida formal.",
        steps: [
            step(1, "Seleccionar almacen", "Filtra el origen de existencias."),
            step(2, "Buscar", "Filtra por descripción o clave."),
            step(3, "Kardex", "Consulta movimientos del artículo."),
            step(4, "Sincronizar", "Recalcula existencias desde movimientos."),
        ],
        tips: ["La foto del artículo se muestra si existe en catálogo.", "Use sincronizar sólo cuando sea necesario."],
        shortcutKeys: [
            shortcut("Alt+F1", "Filtrar artículo"),
        ],
        menuButtons: [
            button("Kardex", "Consulta kardex", "kardex"),
            button("Sincronizar", "Sincroniza existencias", "sincronizar"),
        ],
    }),

    "/entradas": makeHelp({
        title: "Entradas a Almacenes/Exhibidores",
        description: "Registra entradas de inventario por almacen destino.",
        howToStart: "1. Presione Nuevo.\n2. Seleccione almacen destino.\n3. Escanee o busque artículos.\n4. Capture cantidades.",
        howToWork: "Los artículos se registran temporalmente hasta aplicar. El buscador puede mostrar artículos aúnque no tengan existencia.",
        howToEnd: "1. Revise partidas.\n2. Aplique la entrada.\n3. Se registran movimientos de inventario y sincronización.",
        howToPrint: "Genera vista previa o comprobante de entrada.",
        steps: [
            step(1, "Nuevo", "Habilita captura."),
            step(2, "Seleccionar destino", "Elija almacen/exhibidor."),
            step(3, "Agregar artículos", "Escanee o use Alt+F1."),
            step(4, "Aplicar", "Registra movimientos."),
        ],
        tips: ["El buscador no debe abrir si Nuevo está activo.", "Revise almacen destino antes de aplicar."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
            shortcut("Alt+A", "Aplicar entrada"),
            shortcut("Alt+F12", "Eliminar todo"),
        ],
        menuButtons: [
            button("Nuevo", "Nueva entrada", "nuevo"),
            button("Artículos", "Buscar artículo", "buscar"),
            button("Aplicar", "Aplica entrada", "aplicar"),
            button("Menu", "Eliminaciones", "menu"),
        ],
    }),

    "/salidas": makeHelp({
        title: "Salidas de Almacenes/Exhibidores",
        description: "Registra salidas de inventario desde un almacen origen.",
        howToStart: "1. Presione Nuevo.\n2. Seleccione almacen origen.\n3. Escanee o busque artículos.\n4. Capture cantidades y motivo.",
        howToWork: "Las partidas se preparan antes de aplicar. Al aplicar se registran movimientos de salida y sincronización.",
        howToEnd: "Revise partidas y aplique la salida para descontar inventario.",
        howToPrint: "Genera comprobante de salida si el flujo lo solicita.",
        steps: [
            step(1, "Nuevo", "Inicia captura."),
            step(2, "Seleccionar origen", "Elija almacen."),
            step(3, "Agregar artículos", "Escanee o busque."),
            step(4, "Aplicar", "Descuenta inventario."),
        ],
        tips: ["No aplique hasta validar cantidades.", "El buscador sólo debe abrir cuando la captura está activa."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
            shortcut("Alt+A", "Aplicar salida"),
            shortcut("Alt+F12", "Eliminar todo"),
        ],
        menuButtons: [
            button("Nuevo", "Nueva salida", "nuevo"),
            button("Artículos", "Buscar artículo", "buscar"),
            button("Aplicar", "Aplica salida", "aplicar"),
            button("Menu", "Eliminaciones", "menu"),
        ],
    }),

    "/traslados": makeHelp({
        title: "Traslados entre Almacenes/Exhibidores",
        description: "Traslada artículos desde un almacen origen hacia un almacen destino, usando temporales hasta aplicar.",
        howToStart: "1. Seleccione origen.\n2. Seleccione destino.\n3. Escanee o busque artículos.\n4. Revise totales del footer.",
        howToWork: "Los artículos se guardan temporalmente. Al aplicar se generan movimientos de salida y entrada por partida y se sincroniza kardex.",
        howToEnd: "1. Revise origen, destino y cantidades.\n2. Valide acceso si se solicita.\n3. Aplique traslado.\n4. Imprima comprobante si corresponde.",
        howToPrint: "Use Vista previa para imprimir traslado antes o después de aplicar.",
        steps: [
            step(1, "Origen y destino", "Seleccione almacenes distintos."),
            step(2, "Agregar artículos", "Use lector o Alt+F1."),
            step(3, "Revisar tabla", "Use flechas para navegar partidas."),
            step(4, "Aplicar", "Registra movimientos de inventario."),
        ],
        tips: ["F4 regresa al dashboard.", "El footer muestra lector, total registros y total artículos."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
            shortcut("Alt+A", "Aplicar traslado"),
            shortcut("Alt+F7", "Eliminar partida"),
            shortcut("Alt+F12", "Eliminar traslado"),
        ],
        menuButtons: [
            button("Nuevo", "Nuevo traslado", "nuevo"),
            button("Artículos", "Buscar artículo", "buscar"),
            button("Aplicar", "Aplica traslado", "aplicar"),
            button("Vista previa", "Imprime o visualiza", "imprimir"),
            button("Menu", "Eliminaciones", "menu"),
        ],
    }),

    "/etiquetado": makeHelp({
        title: "Módulo de Etiquetado",
        description: "Busca artículos, selecciona tallas y cantidades, e imprime etiquetas.",
        howToStart: "1. Seleccione almacen origen.\n2. Busque artículo.\n3. Seleccione talla y cantidad.\n4. Agregue a lista.",
        howToWork: "El etiquetado puede usarse manualmente desde compras en estatus TR o directamente desde inventario. Imprime etiquetas sin registrar compras.",
        howToEnd: "Revise cantidades y presione imprimir/generar etiquetas.",
        howToPrint: "Envia etiquetas a la impresora configurada.",
        steps: [
            step(1, "Seleccionar almacen", "Define origen de existencias."),
            step(2, "Buscar artículo", "Filtra artículos."),
            step(3, "Capturar cantidades", "Indique etiquetas por talla."),
            step(4, "Imprimir", "Genera etiquetas."),
        ],
        tips: ["Use el almacen correcto antes de buscar.", "Las etiquetas usan precio y código de barras del artículo."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar o nuevo"),
            shortcut("Alt+A", "Agregar artículo"),
            shortcut("Alt+I", "Imprimir etiquetas"),
            shortcut("Alt+F7", "Eliminar partida"),
            shortcut("Alt+F12", "Eliminar todo"),
        ],
        menuButtons: [
            button("Nuevo", "Limpia captura", "nuevo"),
            button("Agregar", "Agrega artículo", "agregar"),
            button("Generar", "Imprime etiquetas", "imprimir"),
            button("Menu", "Eliminaciones", "menu"),
        ],
    }),

    "/tomainventario": makeHelp({
        title: "Toma de Inventario",
        description: "Conteo físico por almacen/zona, comparación contra sistema y aplicación de ajustes.",
        howToStart: "1. Cree o cargue folio.\n2. Seleccione almacen y zona.\n3. Cargue artículos.\n4. Capture conteos.",
        howToWork: "Registre conteos físicos, importe datos si aplica y valide diferencias antes de aplicar ajustes.",
        howToEnd: "Valide diferencias, seleccione conteo definitivo y aplique ajustes.",
        howToPrint: "Imprime reporte de diferencias o toma.",
        steps: [
            step(1, "Crear folio", "Inicia toma."),
            step(2, "Cargar artículos", "Carga artículos por almacen."),
            step(3, "Conteo", "Capture cantidades fisicas."),
            step(4, "Validar", "Revise diferencias."),
            step(5, "Aplicar", "Genera ajustes."),
        ],
        tips: ["No aplique ajustes sin validar diferencias.", "Puede importar conteos desde archivo."],
        shortcutKeys: [
            shortcut("Alt+V", "Validar diferencias"),
        ],
        menuButtons: [
            button("Nuevo", "Nuevo folio", "nuevo"),
            button("Cargar", "Carga artículos", "cargar"),
            button("Validar", "Valida diferencias", "validar"),
            button("Aplicar", "Aplica ajustes", "aplicar"),
        ],
    }),

    "/compras": makeHelp({
        title: "Módulo de Compras",
        description: "Captura compras, partidas, proveedor, pago, almacen destino y aplica integración a inventario.",
        howToStart: "1. Presione Nueva.\n2. Seleccione proveedor.\n3. Capture factura/remisión, tipo de pago y almacen exhibidor destino.\n4. Agregue partidas.",
        howToWork: "Guarde antes de aplicar. Al aplicar, toda compra integra inventario al almacen seleccionado. Si es crédito, se registran cuentas por pagar.",
        howToEnd: "1. Guarde cambios.\n2. Revise partidas y almacen destino.\n3. Aplique compra.\n4. Imprima reporte PDF o use etiquetado si está en TR.",
        howToPrint: "Al aplicar o consultar puede generar reporte PDF. El etiquetado se realiza con botón manual cuando la compra está en TR.",
        steps: [
            step(1, "Nueva compra", "Inicia cabecero."),
            step(2, "Proveedor y pago", "Capture proveedor, factura y tipo de pago."),
            step(3, "Almacen destino", "Seleccione almacen/exhibidor para inventario."),
            step(4, "Partidas", "Capture artículos y cantidades."),
            step(5, "Guardar", "Guarde antes de aplicar."),
            step(6, "Aplicar", "Integra inventario y cuentas por pagar si es crédito."),
            step(7, "Etiquetar", "Disponible en estatus TR."),
        ],
        tips: ["Aplicar siempre integra inventario.", "El almacen exhibidor define destino de movimientos.", "Credito crea cuentas por pagar."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
            shortcut("Alt+G", "Guardar compra"),
            shortcut("Alt+A", "Aplicar compra"),
        ],
        menuButtons: [
            button("Nueva", "Nueva compra", "nuevo"),
            button("Guardar", "Guarda compra", "guardar"),
            button("Aplicar", "Aplica compra", "aplicar"),
            button("Etiquetar", "Abre etiquetado en TR", "etiquetar"),
            button("Imprimir", "Genera PDF", "imprimir"),
            button("Menu", "Eliminaciones", "menu"),
        ],
    }),

    "/parametrosestacion": makeHelp({
        title: "Parámetros de Caja",
        description: "Configuración de caja, impresoras, PC, dominio y parámetros locales de estación.",
        howToStart: "1. Verifique datos de caja.\n2. Revise impresoras.\n3. Valide PrinterTools si aplica.\n4. Guarde.",
        howToWork: "Los parámetros se refrescan al arranque si PrinterTools está disponible. Si no existe, el sistema debe seguir operando con datos temporales.",
        howToEnd: "Guarde cambios y reinicie sesión si requiere aplicar parámetros de estación.",
        steps: [
            step(1, "Revisar caja", "Valide nombre de caja/PC."),
            step(2, "Impresoras", "Configure impresoras de tickets y etiquetas."),
            step(3, "Guardar", "Persistir parámetros."),
        ],
        tips: ["PrinterTools mejora identificación local, pero no debe bloquear operación.", "Revise impresora antes de deploy en tienda."],
        shortcutKeys: [
            shortcut("Alt+G", "Guardar parámetros"),
        ],
        menuButtons: [
            button("Guardar", "Guarda parámetros", "guardar"),
            button("Refrescar", "Obtiene parámetros", "refrescar"),
        ],
    }),

    "/parametrosticketmaster": makeHelp({
        title: "Ticket ventas master",
        description: "Laboratorio para diseñar y probar plantillas JSON de tickets de venta por caja sin modificar el ticket actual del sistema.",
        howToStart: "1. Seleccione caja/equipo.\n2. El sistema carga o crea una plantilla default.\n3. Edite el nombre o JSON de la plantilla.\n4. Use vista previa para validar estructura.",
        howToWork: "El JSON contiene bloques ordenados con tokens como {{encabezado}}, {{partidas}} y {{total}}. La vista previa sustituye datos de ejemplo para revisar forma y lectura del ticket.",
        howToEnd: "Guarde la plantilla cuando el JSON sea válido. Esta pantalla sólo prepara laboratorio; ventas seguira usando el formato actual hasta activar el cambio final.",
        howToPrint: "No imprime directamente. La vista previa simula el ticket para pruebas visuales.",
        steps: [
            step(1, "Seleccionar caja", "Carga configuración de la estación y plantilla de ticket."),
            step(2, "Editar plantilla", "Modifica bloques, orden, visibilidad y textos."),
            step(3, "Vista previa", "Revisa el formato antes de guardar."),
            step(4, "Guardar", "Persiste la plantilla por caja/tipo ticket."),
        ],
        tips: [
            "Si la plantilla no existe, el backend crea una default.",
            "Mantenga los tokens entre llaves dobles para que puedan sustituirse después.",
            "Este laboratorio no altera los tickets productivos actuales.",
        ],
        shortcutKeys: [
            shortcut("Alt+G", "Guardar plantilla"),
        ],
        menuButtons: [
            button("Aplicar", "Guarda plantilla", "guardar"),
            button("Deshacer", "Limpia selección", "deshacer"),
            button("Vista previa", "Simula ticket", "preview"),
        ],
    }),

    "/cortesdecaja": makeHelp({
        title: "Administración de Cortes de Caja",
        description: "Consulta cortes, gestióna aperturas, cierres, retiros y reportes administrativos.",
        howToStart: "1. Valide usuario si requiere gestión.\n2. Seleccione caja o periodo.\n3. Consulte cortes.",
        howToWork: "Sin validación opera como consulta. Con validación se habilitan operaciones de caja.",
        howToEnd: "Revise totales y cierre/retire efectivo sólo con autorización.",
        howToPrint: "Puede reimprimir cortes o reportes desde acciones.",
        steps: [
            step(1, "Validar", "Autoriza gestión de caja."),
            step(2, "Consultar", "Revisa cortes."),
            step(3, "Gestión", "Apertura, cierre o retiro."),
        ],
        tips: ["Los retiros requieren control administrativo.", "Valide datos antes de cerrar caja."],
        shortcutKeys: [],
        menuButtons: [
            button("Validar", "Autoriza gestión", "validar"),
            button("Gestión caja", "Operaciones de caja", "gestión"),
            button("Reportes", "Genera reportes", "reportes"),
        ],
    }),

    "/reporteventasalmacen": makeHelp({
        title: "Reporte de Ventas por Almacen",
        description: "Consulta ventas por almacen, periodo y criterios de reporte.",
        howToStart: "1. Seleccione filtros.\n2. Capture periodo.\n3. Genere búsqueda.",
        howToWork: "Use los filtros para analizar ventas por almacen y generar reporte.",
        howToEnd: "Genere reporte o exporte según herramienta disponible.",
        howToPrint: "Genera reporte de ventas para revision o impresión.",
        steps: [
            step(1, "Filtrar", "Seleccione almacen y periodo."),
            step(2, "Buscar", "Carga información."),
            step(3, "Reporte", "Genera salida."),
        ],
        tips: ["Valide fechas antes de generar reporte."],
        shortcutKeys: [],
        menuButtons: [
            button("Buscar", "Consulta datos", "buscar"),
            button("Reporte", "Genera reporte", "reporte"),
        ],
    }),

    "/reimpresioncaja": makeHelp({
        title: "Reimpresión de Caja",
        description: "Reimprime tickets, preventas, cotizaciones, devoluciones y cortes según filtros.",
        howToStart: "1. Seleccione caja.\n2. Seleccione fechas.\n3. Busque comprobantes.",
        howToWork: "Seleccione un comprobante y use reimprimir. También se puede acceder desde Venta Super para tickets.",
        howToEnd: "Confirme impresora y emita copia.",
        howToPrint: "Reimprime comprobantes existentes.",
        steps: [
            step(1, "Filtrar", "Seleccione caja y fechas."),
            step(2, "Buscar", "Carga comprobantes."),
            step(3, "Reimprimir", "Envia copia a impresora."),
        ],
        tips: ["La reimpresión no modifica registros.", "Use filtros correctos para localizar comprobantes."],
        shortcutKeys: [],
        menuButtons: [
            button("Buscar", "Busca comprobantes", "buscar"),
            button("Reimprimir", "Imprime copia", "reimprimir"),
        ],
    }),

    "/pagocomisiones": makeHelp({
        title: "Pago de Comisiones",
        description: "Calcula y genera reportes de comisiones de vendedores.",
        howToStart: "1. Seleccione periodo.\n2. Seleccione vendedor o filtro.\n3. Calcule comisiones.",
        howToWork: "Revise importes calculados antes de generar reporte o aplicar pago.",
        howToEnd: "Genere reporte y confirme pago según política administrativa.",
        howToPrint: "Genera reporte de comisiones.",
        steps: [
            step(1, "Periodo", "Seleccione fechas."),
            step(2, "Calcular", "Calcula comisiones."),
            step(3, "Reporte", "Genera comprobante o reporte."),
        ],
        tips: ["Revise porcentajes de comisión configurados en accesos o catálogos."],
        shortcutKeys: [],
        menuButtons: [
            button("Nuevo", "Limpia captura", "nuevo"),
            button("Calcular", "Calcula comisiones", "calcular"),
            button("Reporte", "Genera reporte", "reporte"),
        ],
    }),

    "/verificador": makeHelp({
        title: "Verificador de Precios",
        description: "Consulta precio y existencia por código de barras o búsqueda manual.",
        howToStart: "1. Escanee código.\n2. Revise datos y existencias.\n3. Use buscar si no tiene código.",
        howToWork: "Pensado para consulta rapida. No modifica inventario ni ventas.",
        howToEnd: "Queda listo para el siguiente artículo.",
        steps: [
            step(1, "Escanear", "Lee código de barras."),
            step(2, "Buscar", "Alt+F1 abre buscador."),
            step(3, "Consultar", "Muestra precio y existencias."),
        ],
        tips: ["No requiere aplicar movimientos.", "Ideal para mostrador."],
        shortcutKeys: [
            shortcut("Alt+F1", "Buscar artículo"),
        ],
        menuButtons: [
            button("Buscar", "Busca artículo", "buscar"),
        ],
    }),

    "/asistenciapiso": makeHelp({
        title: "Asistencia en piso",
        description: "Asistente para dispositivos portátiles que usa cámara e IA para identificar artículos y consultar costos, medidas y existencias.",
        howToStart: "1. Use dispositivo con cámara.\n2. Toque el artículo en la cámara.\n3. Espere la identificación IA.\n4. Revise tabla de costos y existencias.",
        howToWork: "La IA obtiene clave de artículo y consulta datos de precio definitivo y existencias por medida para apoyar la atención en piso.",
        howToEnd: "Limpie captura o verifique el siguiente artículo.",
        steps: [
            step(1, "Cámara", "Capture artículo."),
            step(2, "Identificar", "IA busca coincidencia."),
            step(3, "Consultar", "Muestra costos y existencias."),
        ],
        tips: ["Requiere cámara funcional y permisos del navegador.", "Pensado para dispositivos portátiles."],
        shortcutKeys: [],
        menuButtons: [
            button("Buscar", "Identifica artículo", "buscar"),
            button("Limpiar", "Limpia captura", "limpiar"),
        ],
    }),

    "/iaentrenamiento": makeHelp({
        title: "Entrenamiento IA",
        description: "Entrena artículos con foto desde computadora o dispositivo móvil. Usa cámara o carga de foto.",
        howToStart: "1. Busque artículo.\n2. Capture o cargue foto.\n3. Verifique imagen y aprendizaje.\n4. Envie a entrenar.",
        howToWork: "El entrenamiento almacena foto principal en Articulos y vectores en SQLite independiente por clave de artículo.",
        howToEnd: "Revise porcentaje de aprendizaje y repita muestras si se requieren más datos.",
        howToPrint: "No imprime; genera datos de aprendizaje IA.",
        steps: [
            step(1, "Buscar artículo", "Carga datos, foto y aprendizaje."),
            step(2, "Capturar foto", "Usa cámara PC o móvil."),
            step(3, "Buscar por foto", "Compara vectores y recupera artículo."),
            step(4, "Entrenar", "Guarda muestra y actualiza aprendizaje."),
        ],
        tips: ["En Android mantenga la misma cámara seleccionada para buscar por foto.", "La foto principal se guarda en la tabla Artículos."],
        shortcutKeys: [],
        menuButtons: [
            button("Buscar", "Busca artículo", "buscar"),
            button("Por foto", "Busca por imagen", "foto"),
            button("Cargar foto", "Carga imagen", "cargar"),
            button("Entrenar", "Envia muestra", "entrenar"),
        ],
    }),

    "/centrocostos": makeHelp({
        title: "Centro de Costos",
        description: "Gestión administrativa del backend de centro de costos, compras, reportes y controles financieros.",
        howToStart: "1. Seleccione herramienta o vista.\n2. Aplique filtros.\n3. Consulte datos.",
        howToWork: "Centraliza procesos administrativos y reportes relacionados con costos y compras.",
        howToEnd: "Guarde o genere reportes según la herramienta activa.",
        howToPrint: "Puede generar reportes dependiendo de la vista seleccionada.",
        steps: [
            step(1, "Seleccionar vista", "Elija herramienta del centro de costos."),
            step(2, "Filtrar", "Defina parámetros."),
            step(3, "Procesar", "Consulte, guarde o genere reporte."),
        ],
        tips: ["Revise filtros antes de procesar información financiera."],
        shortcutKeys: [],
        menuButtons: [
            button("Buscar", "Consulta datos", "buscar"),
            button("Reporte", "Genera reporte", "reporte"),
        ],
    }),

    "/controlasistencia": makeHelp({
        title: "Control de Asistencias",
        description: "Registro de entrada, salida a comer, entrada de comida y salida con captura de foto y validación de clave.",
        howToStart: "1. Seleccione su usuario.\n2. Capture su clave secreta.\n3. Pulse Enter o de clic en el tipo de registro.",
        howToWork: "Los 4 tipos de registro son: Hora Entrada, Hora salida a comer, Hora entrada de comida, Hora salida. Cada tipo sólo puede registrarse una vez al día.",
        howToEnd: "Revise el historial del día para confirmar sus registros.",
        howToPrint: "No imprime; los registros se guardan en la base de datos.",
        steps: [
            step(1, "Seleccionar usuario", "Elija su nombre de la lista."),
            step(2, "Capturar clave", "Ingrese su clave secreta."),
            step(3, "Registrar", "Presione Enter o seleccione el tipo de registro."),
        ],
        tips: ["La clave secreta se valida contra la tabla de Accesos.", "Puede tomar foto con la cámara si está disponible."],
        shortcutKeys: [regresoDashboard],
        menuButtons: [],
    }),

    "/nominas": makeHelp({
        title: "Administración de Nóminas",
        description: "Permite capturar el periodo y configurar parámetros globales (salario diario y horas de jornada) para calcular, guardar, aplicar, consultar el historial y generar el reporte de nóminas por empleado.",
        howToStart: "1. Seleccione las fechas del periodo.\n2. Configure el salario diario y las horas por jornada.\n3. Use Calcular para generar la nómina.",
        howToWork: "El sueldo base se calcula con regla de tres (horas totales entre horas de jornada por salario diario). A cada empleado se le suman comisiones y bonos, y se le restan deducciones de préstamos.",
        howToEnd: "Guarde los cambios para persistir la nómina en proceso y use Aplicar para formalizarla como TERMINADA.",
        howToPrint: "Permite imprimir recibos individuales y generar el reporte PDF del resumen de la nómina.",
        steps: [
            step(1, "Periodo", "Seleccione fecha de inicio y fin."),
            step(2, "Calcular", "Genera la nómina con tiempos, comisiones, bonos y deducciones."),
            step(3, "Guardar/Aplicar", "Persiste la nómina y formaliza el folio."),
        ],
        tips: ["La nómina TERMINADA no se puede eliminar.", "Los parámetros (salario y horas de jornada) se guardan globalmente."],
        shortcutKeys: [
            regresoDashboard,
            shortcut("Alt+F2", "Buscar nóminas guardadas"),
        ],
        menuButtons: [
            button("Calcular", "Calcula la nómina del periodo", "calcular"),
            button("Guardar", "Guarda la nómina en proceso", "guardar"),
            button("Aplicar", "Formaliza la nómina (TERMINADA)", "aplicar"),
            button("PDF", "Genera el reporte PDF", "pdf"),
        ],
    }),
};

export const getHelpByRoute = (pathname) => {
    const normalizedPath = (pathname || "").split("?")[0];
    const helpConfig = HelpConfigMap[normalizedPath];
    if (helpConfig) return helpConfig;

    return {
        title: "Ayuda no disponible",
        description: "Esta pantalla no cuenta con ayuda disponible aún.",
        howToStart: "",
        howToWork: "",
        howToEnd: "",
        howToPrint: "",
        steps: [],
        tips: [],
        shortcutKeys: [regresoDashboard],
        menuButtons: [],
        fields: [],
    };
};

export const getHelpTextToSpeech = (pathname, section = "all") => {
    const help = getHelpByRoute(pathname);

    if (section === "start") {
        return help.howToStart ? `Para comenzar: ${help.howToStart.replace(/\n/g, ". ")}` : "No hay instrucciones de inicio.";
    }
    if (section === "work") {
        return help.howToWork ? `Como trabajar: ${help.howToWork.replace(/\n/g, ". ")}` : "No hay instrucciones de trabajo.";
    }
    if (section === "end") {
        return help.howToEnd ? `Para terminar: ${help.howToEnd.replace(/\n/g, ". ")}` : "No hay instrucciones de cierre.";
    }
    if (section === "print") {
        return help.howToPrint ? `Como imprimir: ${help.howToPrint.replace(/\n/g, ". ")}` : "No hay instrucciones de impresión.";
    }
    if (section === "shortcuts") {
        return help.shortcutKeys?.length
            ? `Atajos de teclado disponibles: ${help.shortcutKeys.map((k) => `${k.key}: ${k.action}`).join(", ")}`
            : "No hay atajos.";
    }
    if (section === "tips") {
        return help.tips?.length ? `Consejos utiles: ${help.tips.join(". ")}` : "No hay consejos.";
    }

    const shortcuts = help.shortcutKeys?.length
        ? `Atajos principales: ${help.shortcutKeys.slice(0, 3).map((k) => k.key).join(", ")}`
        : "";

    return `${help.title}. ${help.description}. ${shortcuts}`;
};
